import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  Star, ShoppingCart, ShieldCheck, Truck, RotateCcw, CheckCircle2, 
  Sparkles, Package, Gift, MapPin, ChevronDown,
  Plus, Minus, Share2, Copy, Lock, Zap, AlertCircle, Globe, Flame, ArrowRight
} from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import ProductCard from '../components/cards/ProductCard';
import { useCart } from '../contexts/CartContext';
import { toast } from 'sonner';

import API from '../config/api';

const DEFAULT_PRODUCT = {
  id: "dhan-yog-bracelet",
  name: "Metal Dhan Yog Bracelet for Women",
  slug: "metal-dhan-yog-bracelet-for-women",
  description_en: "<p>The Paramparik Puja Metal Dhan Yog Bracelet is crafted for those seeking financial growth, stability and prosperity in everyday life. Associated with Goddess Lakshmi and Lord Kubera, it is traditionally worn to attract wealth, ease obstacles in business and career, and bring a quiet sense of financial confidence.</p><p>Each bracelet is handcrafted using pure metal beads and finished to a smooth, comfortable fit. Before it reaches you, every piece is energised by qualified Vedic pandits through a traditional Prana Pratishtha ritual.</p>",
  description_hi: "<p>पारंपरिक पूजा मेटल धन योग कंगन महिलाओं के लिए विशेष रूप से आर्थिक समृद्धि, व्यापार वृद्धि और लक्ष्मी-कुबेर कृपा प्राप्ति हेतु सिद्ध किया गया है। यह हिमालयी वैदिक पंडितों द्वारा प्राण प्रतिष्ठा अनुष्ठान के बाद शुद्ध रूप से भेजा जाता है।</p>",
  benefits_en: "<ul><li>Believed to attract wealth, prosperity and long-term financial stability</li><li>Traditionally worn to ease obstacles related to money, business and career growth</li><li>Associated with balancing the influence of Jupiter (Guru) — planet of abundance</li><li>Said to boost confidence during important financial decisions and negotiations</li><li>Commonly worn during Diwali, Dhanteras and auspicious life occasions</li></ul>",
  benefits_hi: "<ul><li>धन, समृद्धि और व्यापार में स्थिरता प्रदान करने में सहायक</li><li>आर्थिक रुकावटों और करियर में आने वाली बाधाओं को शांत करता है</li><li>गुरु एवं शुक्र ग्रह के शुभ प्रभावों को जागृत करने में उपयोगी</li><li>दैनिक जीवन, दीपावली, धनतेरस एवं शुभ अवसरों पर धारण योग्य</li></ul>",
  how_to_use_en: "<ol><li>Wear the bracelet on your right wrist for auspicious energy alignment.</li><li>Remove before bathing or swimming to maintain the sacred metal finish.</li><li>Chant the Mahalakshmi mantra 11 times when wearing for the first time.</li><li>Clean gently with a soft dry cloth — avoid chemical contact.</li></ol>",
  usp_en: "",
  usp_hi: "",
  price: 1299,
  mrp: 1999,
  category: "bracelets",
  category_name: "Dhan Yog Collection",
  image_url: "https://images.unsplash.com/photo-1611591475194-e0eb37470f5e?q=80&w=800",
  images: [
    "https://images.unsplash.com/photo-1611591475194-e0eb37470f5e?q=80&w=800",
    "https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?q=80&w=800",
    "https://images.unsplash.com/photo-1605100804763-247f67b3557e?q=80&w=800",
    "https://images.unsplash.com/photo-1600010476008-8e684074bba6?q=80&w=800"
  ],
  rating: 4.7,
  ratingCount: 1842,
  stock: 85,
  origin: "Haridwar, Uttarakhand",
  mantra: "ॐ श्रीं ह्रीं श्रीं कमले कमलालये प्रसीद प्रसीद श्रीं ह्रीं श्रीं ॐ महालक्ष्म्यै नमः",
  mantra_translit: "Om Shreem Hreem Shreem Kamale Kamalaaye Praseed Praseed Shreem Hreem Shreem Om Mahalakshmyai Namah",
  mantra_note: "Chant 11 or 108 times, preferably at sunrise, while wearing the bracelet for the first time.",
  faqs: [
    { question: "Is this product suitable for everyday wear?", answer: "Yes, it is crafted for comfortable daily wear. Avoid direct contact with swimming water, perfumes or chemicals to preserve the luster." },
    { question: "Has the product been energised before shipping?", answer: "Yes — every item undergoes Prana Pratishtha performed by qualified Vedic pandits in Haridwar prior to dispatch." },
    { question: "What is your return and exchange policy?", answer: "We offer a 7-day easy return from the date of delivery, provided the product is unused and in original condition." },
    { question: "Do you offer Cash on Delivery (COD)?", answer: "Yes, COD is available across over 19,000 PIN codes in India." },
    { question: "How long will delivery take?", answer: "Orders are dispatched within 24 hours and typically arrive within 4–7 business days Pan-India." }
  ],
  testimonials: [
    { name: "Priya S.", location: "Bengaluru", quote: "Bought this for my mother before Dhanteras — she loves how elegant it looks and it arrived beautifully packed with the energisation certificate.", verified: true },
    { name: "Rohit M.", location: "Pune", quote: "Quality feels premium and true to the pictures. The energisation note and mantra card included was a nice touch.", verified: true },
    { name: "Ankit V.", location: "Delhi", quote: "Ordered as a gift for my wife's birthday. Fast delivery and she wears it daily now. Satisfied with authentic finish.", verified: true },
    { name: "Meera K.", location: "Hyderabad", quote: "Second time ordering from Paramparik Puja. Consistent quality and COD option is very convenient.", verified: true }
  ]
};

const DEFAULT_PRODUCT_ADDONS = [
  { id: 'addon_gangajal_prod', name: 'Consecrated Gangotri Gangajal (100ml)', price: 101, description: 'Blessed during morning Ganga Aarti in Haridwar.' },
  { id: 'addon_mauli_prod', name: 'Vedic Raksha Sutra (Sacred Red Thread)', price: 51, description: 'Chanted with Vedic mantras for divine protection.' },
  { id: 'addon_diya_prod', name: 'Handcrafted Pure Brass Diya', price: 199, description: 'Pure brass diya for daily household puja & aarti.' }
];

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const base = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
  return `${base}${url}`;
};

// Check if a rich text or string content field has meaningful content (not empty HTML tags)
const hasValidContent = (content) => {
  if (!content) return false;
  if (typeof content === 'string') {
    const stripped = content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim();
    return stripped.length > 0;
  }
  if (Array.isArray(content)) {
    return content.some(item => hasValidContent(item));
  }
  return false;
};

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('desc');
  const [contentLang, setContentLang] = useState('en'); // 'en' or 'hi' toggle specifically for dynamic content
  const [giftWrap, setGiftWrap] = useState(false);
  const [pincode, setPincode] = useState('');
  const [pincodeStatus, setPincodeStatus] = useState(null);
  const [isCheckingPincode, setIsCheckingPincode] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [globalFaqs, setGlobalFaqs] = useState([]);
  const [globalTestimonials, setGlobalTestimonials] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [linkedPujas, setLinkedPujas] = useState([]);

  const handleCopyLink = () => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedLink(true);
      toast.success('Product link copied to clipboard!');
      setTimeout(() => setCopiedLink(false), 2000);
    }).catch(() => {
      toast.error('Failed to copy link');
    });
  };

  // Fetch Product Details, Global FAQs, Global Testimonials, and Linked Pujas
  useEffect(() => {
    const fetchProductDetails = async () => {
      setLoading(true);
      window.scrollTo(0, 0);
      try {
        const idToFetch = productId || 'dhan-yog-bracelet';
        const [prodRes, faqRes, testiRes] = await Promise.all([
          axios.get(`${API}/products/${idToFetch}`),
          axios.get(`${API}/faqs`).catch(() => ({ data: [] })),
          axios.get(`${API}/testimonials`).catch(() => ({ data: [] }))
        ]);
        
        let pData = prodRes.data;
        if (!pData || pData.detail === 'Product not found') {
          pData = { ...DEFAULT_PRODUCT, id: idToFetch };
        }

        // Fill images array if missing
        if (!pData.images || pData.images.length === 0) {
          pData.images = [
            pData.image_url || DEFAULT_PRODUCT.image_url,
            "https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?q=80&w=800",
            "https://images.unsplash.com/photo-1605100804763-247f67b3557e?q=80&w=800",
            "https://images.unsplash.com/photo-1600010476008-8e684074bba6?q=80&w=800"
          ];
        }

        setProduct(pData);
        setGlobalFaqs(Array.isArray(faqRes.data) && faqRes.data.length > 0 ? faqRes.data : DEFAULT_PRODUCT.faqs);
        setGlobalTestimonials(Array.isArray(testiRes.data) && testiRes.data.length > 0 ? testiRes.data : DEFAULT_PRODUCT.testimonials);
        setActiveImageIndex(0);
      } catch (err) {
        console.error("Error fetching product details:", err);
        setProduct(DEFAULT_PRODUCT);
        setGlobalFaqs(DEFAULT_PRODUCT.faqs);
        setGlobalTestimonials(DEFAULT_PRODUCT.testimonials);
        setActiveImageIndex(0);
      } finally {
        setLoading(false);
      }
    };

    const fetchRelated = async () => {
      try {
        const response = await axios.get(`${API}/products`, { params: { limit: 4 } });
        const list = Array.isArray(response.data) ? response.data : (response.data.products || []);
        setRelatedProducts(list.filter(p => p.id !== productId && p.slug !== productId).slice(0, 4));
      } catch (e) {
        console.error("Related products error:", e);
      }
    };

    const fetchLinkedPujas = async () => {
      try {
        const response = await axios.get(`${API}/pujas`);
        const list = Array.isArray(response.data) ? response.data : (response.data.pujas || []);
        setLinkedPujas(list);
      } catch (e) {
        console.error("Linked pujas fetch error:", e);
      }
    };

    fetchProductDetails();
    fetchRelated();
    fetchLinkedPujas();
  }, [productId]);

  const handleQuantityChange = (type) => {
    if (type === 'dec' && quantity > 1) setQuantity(quantity - 1);
    if (type === 'inc' && quantity < 10) setQuantity(quantity + 1);
  };

  const addonsList = (Array.isArray(product?.addons) && product.addons.length > 0)
    ? product.addons
    : DEFAULT_PRODUCT_ADDONS;

  const toggleAddon = (addon) => {
    setSelectedAddons(prev => {
      const match = prev.find(a => (a.id || a.name) === (addon.id || addon.name));
      if (match) {
        return prev.filter(a => (a.id || a.name) !== (addon.id || addon.name));
      }
      return [...prev, addon];
    });
  };

  const addonsTotal = selectedAddons.reduce((sum, a) => sum + (Number(a.price) || 0), 0);
  const effectiveBasePrice = Number(product?.price || 0);
  const effectiveUnitPrice = effectiveBasePrice + addonsTotal + (giftWrap ? 49 : 0);

  const handleAddToCart = () => {
    if (!product) return;
    const cartItemId = `${product.id}${selectedAddons.length ? '-' + selectedAddons.map(a => a.id || a.name).sort().join('-') : ''}${giftWrap ? '-gift' : ''}`;
    addToCart({
      ...product,
      cartItemId,
      price: effectiveUnitPrice,
      selectedAddons: selectedAddons,
      has_gift_wrap: giftWrap
    }, quantity);
    toast.success(`${product.name} added to cart!`, {
      description: `${quantity} unit(s)${selectedAddons.length > 0 ? ` with ${selectedAddons.length} add-on(s)` : ''} • ₹${(effectiveUnitPrice * quantity).toLocaleString('en-IN')}`,
    });
  };

  const handleBuyNow = () => {
    handleAddToCart();
    navigate('/cart');
  };

  const handlePincodeCheck = (e) => {
    e.preventDefault();
    const cleanPin = pincode.trim();
    if (!cleanPin) {
      setPincodeStatus({
        success: false,
        message: "Please enter your 6-digit delivery PIN code."
      });
      return;
    }

    if (/^\d{6}$/.test(cleanPin)) {
      setIsCheckingPincode(true);
      setTimeout(() => {
        setIsCheckingPincode(false);
        setPincodeStatus({
          success: true,
          pin: cleanPin,
          message: "Delivery Available — Arrives in 4–7 business days via Express Courier.",
          cod: true
        });
      }, 300);
    } else {
      setPincodeStatus({
        success: false,
        message: "Invalid PIN code. Please enter a valid 6-digit Indian PIN code."
      });
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-[#FBF4E7] pt-20 pb-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-[#6E1B34] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-xs sm:text-sm font-semibold text-[#4E1225]">Loading Sacred Product Details...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const images = product.images && product.images.length > 0 ? product.images : [product.image_url];
  const activeImage = images[activeImageIndex] || product.image_url;
  const mrp = product.mrp || Math.round(product.price * 1.35);
  const discount = product.discount || Math.round(((mrp - product.price) / mrp) * 100);

  const hasBenefits = hasValidContent(product.benefits_en) || hasValidContent(product.benefits_hi) || hasValidContent(product.benefits);
  const hasHowTo = hasValidContent(product.how_to_use_en) || hasValidContent(product.how_to_use_hi) || hasValidContent(product.howToUse);
  const hasUsp = hasValidContent(product.usp_en) || hasValidContent(product.usp_hi);

  // Dynamic Bilingual Content from backend
  const currentDescription = contentLang === 'hi' 
    ? (hasValidContent(product.description_hi) ? product.description_hi : (product.description_en || product.description || '')) 
    : (hasValidContent(product.description_en) ? product.description_en : (product.description_hi || product.description || ''));

  const currentBenefits = contentLang === 'hi' 
    ? (hasValidContent(product.benefits_hi) ? product.benefits_hi : (product.benefits_en || product.benefits || ''))
    : (hasValidContent(product.benefits_en) ? product.benefits_en : (product.benefits_hi || product.benefits || ''));

  const currentHowTo = contentLang === 'hi' 
    ? (hasValidContent(product.how_to_use_hi) ? product.how_to_use_hi : (product.how_to_use_en || product.howToUse || ''))
    : (hasValidContent(product.how_to_use_en) ? product.how_to_use_en : (product.how_to_use_hi || product.howToUse || ''));

  const currentUsp = contentLang === 'hi'
    ? (hasValidContent(product.usp_hi) ? product.usp_hi : (product.usp_en || ''))
    : (hasValidContent(product.usp_en) ? product.usp_en : (product.usp_hi || ''));

  // Ensure active tab points to an available tab without illegal hook placement
  const effectiveActiveTab = (activeTab === 'usp' && !hasUsp)
    ? 'desc'
    : (activeTab === 'howto' && !hasHowTo)
      ? 'desc'
      : (activeTab === 'benefits' && !hasBenefits)
        ? 'desc'
        : activeTab;

  // FAQs: Use product-specific if available, otherwise pick global site FAQs
  const customFaqs = Array.isArray(product.faqs) ? product.faqs.filter(f => f && f.question && f.question.trim()) : [];
  const faqsList = customFaqs.length > 0 ? customFaqs : (globalFaqs.length > 0 ? globalFaqs : DEFAULT_PRODUCT.faqs);

  // Testimonials: Use product-specific if available, otherwise pick global site Testimonials
  const customTestimonials = Array.isArray(product.testimonials) ? product.testimonials.filter(t => t && (t.name?.trim() || t.quote?.trim())) : [];
  const testimonialsList = customTestimonials.length > 0 ? customTestimonials : (globalTestimonials.length > 0 ? globalTestimonials : DEFAULT_PRODUCT.testimonials);

  return (
    <>
      <Navbar />

      <div className="bg-[#FBF4E7] min-h-screen pt-3 sm:pt-6 pb-16">
        
        {/* Breadcrumb Navigation */}
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-1">
          <nav className="flex items-center gap-1.5 text-[11px] sm:text-xs text-[#83725B]">
            <Link to="/" className="hover:text-[#D9711F] transition-colors">Home</Link>
            <span>/</span>
            <Link to="/store" className="hover:text-[#D9711F] transition-colors">Store</Link>
            <span>/</span>
            <span className="text-[#2A1F16] font-semibold truncate max-w-[180px] sm:max-w-none">{product.name}</span>
          </nav>
        </div>

        {/* ═══════════════════ PRODUCT HERO SECTION ═══════════════════ */}
        <section className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-1 sm:py-3">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 lg:gap-10 items-start">
            
            {/* ─── Compact Clean Gallery Column ─── */}
            <div className="lg:col-span-5 w-full max-w-md mx-auto lg:max-w-none space-y-2.5 lg:sticky lg:top-20">
              
              {/* Main Image Container */}
              <div className="relative aspect-square max-h-[280px] sm:max-h-[380px] w-full rounded-2xl overflow-hidden shadow-sm bg-white">
                <img
                  src={resolveUrl(activeImage)}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Compact Sleek Thumbnails Strip */}
              {images.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-1 pt-1 justify-center scrollbar-none">
                  {images.map((img, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setActiveImageIndex(idx)}
                      className={`relative w-12 h-12 sm:w-14 sm:h-14 rounded-xl overflow-hidden flex-shrink-0 border-2 transition-all cursor-pointer ${
                        activeImageIndex === idx 
                          ? 'border-[#D9711F] ring-2 ring-[#D9711F]/30 scale-105 shadow-xs' 
                          : 'border-transparent opacity-70 hover:opacity-100'
                      }`}
                      aria-label={`Select product image ${idx + 1}`}
                    >
                      <img src={resolveUrl(img)} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* ─── Info Column (Right 7 cols on lg) ─── */}
            <div className="lg:col-span-7 bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-7 border border-[#E7D9BC] shadow-xs">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] sm:text-xs font-bold text-[#D9711F] uppercase tracking-widest block">
                  {product.category_name || product.categoryLabel || product.category || 'Sacred Collection'}
                </span>

                {/* Bestseller Badge (Point 6) */}
                {product.bestseller && (
                  <span className="inline-flex items-center gap-1 bg-[#FCEEDF] text-[#B85A15] border border-[#F2A65A]/40 font-bold px-2 py-0.5 rounded-md text-[10px]">
                    <Star className="w-3 h-3 fill-current text-[#D9711F]" />
                    Bestseller
                  </span>
                )}
              </div>

              {/* Dynamic Product Name Heading */}
              <h1 className="font-heading text-lg sm:text-2xl md:text-3xl font-bold text-[#4E1225] leading-snug mb-2">
                {product.name}
              </h1>

              {/* Rating Row */}
              <div className="flex items-center gap-2 mb-2.5 text-xs">
                <div className="flex text-[#D9711F]">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-3.5 h-3.5 fill-current" />
                  ))}
                </div>
                <span className="font-bold text-[#2A1F16]">{product.rating || 4.7}</span>
                <span className="text-[#83725B]">({(product.ratingCount || 1842).toLocaleString()} reviews)</span>
                <a href="#reviews-section" className="text-[#6E1B34] underline font-medium ml-1 text-xs">Read reviews</a>
              </div>

              {/* Pricing Row */}
              <div className="flex items-baseline gap-2 sm:gap-3 mb-1 flex-wrap">
                <span className="font-price text-xl sm:text-3xl font-bold text-[#4E1225]">
                  ₹{(product.price + addonsTotal).toLocaleString('en-IN')}
                </span>
                {addonsTotal > 0 && (
                  <span className="text-[11px] text-[#6E1B34] font-semibold bg-[#FAF7F0] border border-[#E7D9BC] px-2 py-0.5 rounded-md">
                    (Includes ₹{addonsTotal} add-ons)
                  </span>
                )}
                {mrp > product.price && (
                  <span className="text-xs sm:text-base text-[#83725B] line-through font-price">
                    ₹{(mrp + addonsTotal).toLocaleString('en-IN')}
                  </span>
                )}
                {discount > 0 && (
                  <span className="text-[11px] sm:text-xs font-extrabold text-[#2E7D45] bg-[#2E7D45]/10 px-2 py-0.5 rounded-md">
                    {discount}% OFF
                  </span>
                )}
              </div>
              <p className="text-[11px] sm:text-xs text-[#83725B] mb-3.5">Inclusive of all taxes &middot; Free Pan-India shipping above ₹999</p>

              {/* 🎁 Gift with Purchase Callout Banner (Point 9) */}
              {product.gift_product && product.gift_product.name && (
                <div className="p-3 bg-gradient-to-r from-[#FFF8ED] to-[#FFF2E0] border border-[#F2A65A]/50 rounded-xl mb-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg overflow-hidden bg-white border border-[#E7D9BC] flex-shrink-0">
                    <img
                      src={resolveUrl(product.gift_product.image_url)}
                      alt={product.gift_product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 text-xs font-bold text-[#6E1B34]">
                      <Gift className="w-3.5 h-3.5 text-[#D9711F]" />
                      <span>Free Sacred Gift Included!</span>
                    </div>
                    <p className="text-[11px] text-[#2A1F16] font-medium truncate">
                      {product.gift_product.name} (Worth ₹{product.gift_product.price})
                    </p>
                  </div>
                </div>
              )}

              {/* Urgency Badge */}
              <div className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-semibold text-[#B85A15] bg-[#FCEEDF] border border-[#F2A65A]/40 px-2.5 py-1 rounded-xl mb-4">
                <Package className="w-3.5 h-3.5 text-[#D9711F] flex-shrink-0" />
                <span>126 people ordered this item in the last 7 days</span>
              </div>

              {/* Key Trust Notes */}
              <div className="bg-[#FBF4E7] p-3 sm:p-4 rounded-xl border border-[#E7D9BC] space-y-1.5 mb-4 text-xs text-[#2A1F16]">
                <div className="flex items-center gap-2 font-medium">
                  <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#2E7D45] flex-shrink-0" />
                  <span>Energised by Vedic Pandits — Prana Pratishtha performed</span>
                </div>
                <div className="flex items-center gap-2 font-medium">
                  <Truck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#6E1B34] flex-shrink-0" />
                  <span>Dispatched in 24 hrs &middot; Free Pan-India express shipping</span>
                </div>
              </div>

              {/* Quantity Selector & CTAs (Fully Responsive) */}
              <div className="space-y-3 mb-4">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 sm:gap-3">
                  {/* Quantity Counter */}
                  <div className="flex items-center justify-between sm:justify-start border-2 border-[#E7D9BC] rounded-xl overflow-hidden h-11 sm:h-12 bg-[#FBF4E7]/40 flex-shrink-0 px-1">
                    <span className="text-xs text-[#83725B] font-semibold pl-2 pr-1 sm:hidden">Quantity:</span>
                    <div className="flex items-center">
                      <button
                        type="button"
                        onClick={() => handleQuantityChange('dec')}
                        className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center text-[#6E1B34] hover:bg-[#F3E7D0] rounded-lg transition-colors"
                        aria-label="Decrease quantity"
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="w-7 text-center font-bold text-xs sm:text-sm text-[#2A1F16]">{quantity}</span>
                      <button
                        type="button"
                        onClick={() => handleQuantityChange('inc')}
                        className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center text-[#6E1B34] hover:bg-[#F3E7D0] rounded-lg transition-colors"
                        aria-label="Increase quantity"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Add to Cart & Buy Now Buttons */}
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 flex-1 min-w-0">
                    <button
                      type="button"
                      onClick={handleAddToCart}
                      className="h-11 sm:h-12 rounded-xl border-2 border-[#6E1B34] text-[#6E1B34] font-bold text-xs sm:text-sm hover:bg-[#6E1B34] hover:text-white transition-all flex items-center justify-center gap-1.5 shadow-xs active:scale-95"
                      data-testid="add-to-cart-btn"
                    >
                      <ShoppingCart className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="truncate">Add to Cart</span>
                    </button>
                    
                    <button
                      type="button"
                      onClick={handleBuyNow}
                      className="h-11 sm:h-12 rounded-xl bg-[#6E1B34] text-white font-bold text-xs sm:text-sm hover:bg-[#4E1225] transition-all flex items-center justify-center gap-1.5 shadow-md active:scale-95"
                      data-testid="buy-now-btn"
                    >
                      <Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#F2A65A] flex-shrink-0" />
                      <span className="truncate">Buy Now</span>
                    </button>
                  </div>
                </div>

                {/* Gift Wrapping Option */}
                <label className="flex items-center gap-2 text-xs text-[#2A1F16] cursor-pointer pt-0.5 select-none">
                  <input
                    type="checkbox"
                    checked={giftWrap}
                    onChange={(e) => setGiftWrap(e.target.checked)}
                    className="w-3.5 h-3.5 accent-[#6E1B34] rounded"
                  />
                  <Gift className="w-3.5 h-3.5 text-[#D9711F] flex-shrink-0" />
                  <span>Add gift packaging (+₹49) with sacred blessing card</span>
                </label>
              </div>

              {/* ═══════════════════ SHARE OPTIONS (Point 4: WhatsApp, Facebook, Twitter, Copy) ═══════════════════ */}
              <div className="pt-3 pb-1 flex flex-wrap items-center justify-between gap-2 border-t border-[#E7D9BC]/60">
                <span className="text-xs font-semibold text-[#83725B] flex items-center gap-1.5">
                  <Share2 className="w-3.5 h-3.5 text-[#6E1B34]" />
                  Share with Family &amp; Friends:
                </span>
                
                <div className="flex items-center gap-2">
                  {/* WhatsApp */}
                  <a
                    href={`https://api.whatsapp.com/send?text=${encodeURIComponent(`Check out ${product.name} on Paramparik Pooja: ` + (typeof window !== 'undefined' ? window.location.href : ''))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-8 h-8 rounded-full bg-[#25D366]/10 hover:bg-[#25D366] text-[#25D366] hover:text-white flex items-center justify-center border border-[#25D366]/30 transition-all shadow-2xs hover:scale-105"
                    title="Share on WhatsApp"
                    aria-label="Share on WhatsApp"
                    data-testid="share-whatsapp-btn"
                  >
                    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </a>

                  {/* Facebook */}
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-8 h-8 rounded-full bg-[#1877F2]/10 hover:bg-[#1877F2] text-[#1877F2] hover:text-white flex items-center justify-center border border-[#1877F2]/30 transition-all shadow-2xs hover:scale-105"
                    title="Share on Facebook"
                    aria-label="Share on Facebook"
                    data-testid="share-facebook-btn"
                  >
                    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>

                  {/* Twitter / X */}
                  <a
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Experience authentic ${product.name} from Paramparik Pooja: `)}&url=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-8 h-8 rounded-full bg-black/5 hover:bg-black text-gray-800 hover:text-white flex items-center justify-center border border-gray-300 transition-all shadow-2xs hover:scale-105"
                    title="Share on Twitter / X"
                    aria-label="Share on Twitter"
                    data-testid="share-twitter-btn"
                  >
                    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </a>

                  {/* Copy Link */}
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    className={`h-8 px-2.5 rounded-full flex items-center gap-1 text-xs font-semibold border transition-all shadow-2xs cursor-pointer ${
                      copiedLink
                        ? 'bg-[#2E7D45] text-white border-[#2E7D45]'
                        : 'bg-white hover:bg-[#F3E7D0] text-[#4E1225] border-[#E7D9BC]'
                    }`}
                    title="Copy Link"
                    aria-label="Copy Link"
                    data-testid="share-copy-btn"
                  >
                    {copiedLink ? (
                      <>
                        <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5 text-[#6E1B34]" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* ═══════════════════ FULLY RESPONSIVE CHECK AVAILABILITY PINCODE SECTION ═══════════════════ */}
              <div className="pt-3.5 border-t border-[#E7D9BC]">
                <label className="block text-xs font-bold text-[#4E1225] mb-1.5 flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-[#D9711F] flex-shrink-0" />
                  <span>Check Delivery Availability &amp; Estimated Date</span>
                </label>
                
                <form onSubmit={handlePincodeCheck} className="flex flex-col sm:flex-row gap-2 w-full max-w-md">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      placeholder="Enter 6-digit PIN code"
                      value={pincode}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '');
                        setPincode(val);
                        if (pincodeStatus) setPincodeStatus(null);
                      }}
                      className="w-full border-2 border-[#E7D9BC] rounded-xl px-3 py-2 text-xs sm:text-sm text-[#2A1F16] bg-[#FBF4E7]/60 focus:bg-white focus:outline-none focus:border-[#D9711F] transition-all"
                      data-testid="pincode-input"
                    />
                    {pincode && (
                      <button
                        type="button"
                        onClick={() => { setPincode(''); setPincodeStatus(null); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-[#83725B] hover:text-[#4E1225] p-1 font-medium"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isCheckingPincode}
                    className="w-full sm:w-auto px-4 py-2 bg-[#4E1225] hover:bg-[#6E1B34] text-white text-xs sm:text-sm font-bold rounded-xl transition-all shadow-sm hover:shadow active:scale-95 flex-shrink-0 flex items-center justify-center gap-1.5 disabled:opacity-75"
                    data-testid="check-pincode-btn"
                  >
                    {isCheckingPincode ? (
                      <>
                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Checking...</span>
                      </>
                    ) : (
                      <span>Check Availability</span>
                    )}
                  </button>
                </form>

                {/* Result Feedback Banner */}
                {pincodeStatus && (
                  <div className={`mt-2.5 p-2.5 rounded-xl border text-xs leading-relaxed flex items-start gap-2 transition-all ${
                    pincodeStatus.success 
                      ? 'bg-[#EBF7EE] border-[#2E7D45]/30 text-[#1B5E20]' 
                      : 'bg-red-50 border-red-200 text-red-700'
                  }`}>
                    {pincodeStatus.success ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#2E7D45] flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertCircle className="w-3.5 h-3.5 text-red-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className="font-semibold text-xs">{pincodeStatus.message}</p>
                      {pincodeStatus.success && (
                        <p className="text-[11px] text-[#2E7D45]/90 mt-0.5">
                          ✓ Cash on Delivery (COD) is available &middot; Free shipping on this order
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Trust Features Bar */}
              <div className="grid grid-cols-3 gap-2 pt-3.5 mt-3.5 border-t border-[#E7D9BC] text-center text-[10px] sm:text-[11px] text-[#83725B]">
                <div className="flex flex-col items-center gap-1">
                  <Lock className="w-3.5 h-3.5 text-[#6E1B34]" />
                  <span className="leading-tight">100% Secure</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <RotateCcw className="w-3.5 h-3.5 text-[#6E1B34]" />
                  <span className="leading-tight">7-Day Return</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#6E1B34]" />
                  <span className="leading-tight">COD Available</span>
                </div>
              </div>

            </div>

          </div>
        </section>

        {/* ═══════════════════ TABS SECTION (DYNAMIC CONTENT & BILINGUAL) ═══════════════════ */}
        <section className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-5 sm:py-8">
          <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-8 border border-[#E7D9BC] shadow-xs">
            
            {/* Nav Tabs & Hindi Content Toggle Switch on Description Bar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-[#E7D9BC] pb-3 mb-4 sm:mb-6 gap-3">
              
              <div className="flex gap-2 sm:gap-4 overflow-x-auto scrollbar-none">
                <button
                  type="button"
                  onClick={() => setActiveTab('desc')}
                  className={`pb-2.5 px-2 text-xs sm:text-sm font-bold transition-all relative whitespace-nowrap cursor-pointer ${
                    effectiveActiveTab === 'desc' ? 'text-[#D9711F]' : 'text-[#83725B] hover:text-[#2A1F16]'
                  }`}
                >
                  {contentLang === 'hi' ? 'विवरण (Description)' : 'Description'}
                  {effectiveActiveTab === 'desc' && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D9711F]" />
                  )}
                </button>

                {hasBenefits && (
                  <button
                    type="button"
                    onClick={() => setActiveTab('benefits')}
                    className={`pb-2.5 px-2 text-xs sm:text-sm font-bold transition-all relative whitespace-nowrap cursor-pointer ${
                      effectiveActiveTab === 'benefits' ? 'text-[#D9711F]' : 'text-[#83725B] hover:text-[#2A1F16]'
                    }`}
                  >
                    {contentLang === 'hi' ? 'महत्व एवं लाभ (Benefits)' : 'Benefits & Astrology'}
                    {effectiveActiveTab === 'benefits' && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D9711F]" />
                    )}
                  </button>
                )}

                {hasHowTo && (
                  <button
                    type="button"
                    onClick={() => setActiveTab('howto')}
                    className={`pb-2.5 px-2 text-xs sm:text-sm font-bold transition-all relative whitespace-nowrap cursor-pointer ${
                      effectiveActiveTab === 'howto' ? 'text-[#D9711F]' : 'text-[#83725B] hover:text-[#2A1F16]'
                    }`}
                  >
                    {contentLang === 'hi' ? 'धारण विधि (How to Use)' : 'How to Use'}
                    {effectiveActiveTab === 'howto' && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D9711F]" />
                    )}
                  </button>
                )}

                {hasUsp && (
                  <button
                    type="button"
                    onClick={() => setActiveTab('usp')}
                    className={`pb-2.5 px-2 text-xs sm:text-sm font-bold transition-all relative whitespace-nowrap cursor-pointer ${
                      effectiveActiveTab === 'usp' ? 'text-[#D9711F]' : 'text-[#83725B] hover:text-[#2A1F16]'
                    }`}
                    data-testid="product-tab-usp"
                  >
                    {contentLang === 'hi' ? 'विशिष्टता (USP)' : 'USP'}
                    {effectiveActiveTab === 'usp' && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D9711F]" />
                    )}
                  </button>
                )}
              </div>

              {/* Toggle Switch Button on Description Bar */}
              <div className="flex items-center gap-2.5 bg-[#FBF4E7] px-3.5 py-1.5 rounded-full border border-[#E7D9BC] self-start sm:self-auto shadow-2xs">
                <Globe className="w-3.5 h-3.5 text-[#D9711F]" />
                <span className="text-xs font-semibold text-[#4E1225]">
                  {contentLang === 'hi' ? 'हिंदी सामग्री:' : 'Hindi Content:'}
                </span>
                
                <button
                  type="button"
                  role="switch"
                  aria-checked={contentLang === 'hi'}
                  onClick={() => setContentLang(prev => prev === 'hi' ? 'en' : 'hi')}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none cursor-pointer ${
                    contentLang === 'hi' ? 'bg-[#6E1B34]' : 'bg-[#D6C4A5]'
                  }`}
                  title="Toggle Hindi Content"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition duration-200 shadow-sm ${
                      contentLang === 'hi' ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                
                <span className={`text-xs font-bold ${contentLang === 'hi' ? 'text-[#6E1B34]' : 'text-[#83725B]'}`}>
                  {contentLang === 'hi' ? 'हिंदी (ON)' : 'OFF'}
                </span>
              </div>

            </div>

            {/* Tab Contents with Rich Text Rendering */}
            {effectiveActiveTab === 'desc' && (
              <div className="space-y-3 text-xs sm:text-sm text-[#2A1F16] leading-relaxed max-w-4xl prose prose-sm max-w-none">
                <h3 className="font-heading text-sm sm:text-base font-bold text-[#4E1225] not-prose mb-1">
                  {contentLang === 'hi' ? 'इस दिव्य उत्पाद के बारे में (विवरण)' : 'About This Sacred Offering'}
                </h3>
                {typeof currentDescription === 'string' ? (
                  <div dangerouslySetInnerHTML={{ __html: currentDescription }} />
                ) : (
                  <p>{product.description}</p>
                )}
              </div>
            )}

            {hasBenefits && effectiveActiveTab === 'benefits' && (
              <div className="space-y-3 max-w-4xl prose prose-sm max-w-none">
                <h3 className="font-heading text-sm sm:text-base font-bold text-[#4E1225] not-prose mb-2">
                  {contentLang === 'hi' ? 'धार्मिक महत्व एवं ज्योतिषीय प्रभाव (महत्व व लाभ)' : 'Sacred Benefits & Astrological Significance'}
                </h3>
                {typeof currentBenefits === 'string' ? (
                  <div dangerouslySetInnerHTML={{ __html: currentBenefits }} />
                ) : Array.isArray(currentBenefits) ? (
                  <ul className="space-y-2 not-prose">
                    {currentBenefits.map((b, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm text-[#2A1F16]">
                        <span className="w-1.5 h-1.5 bg-[#B98B3E] rotate-45 mt-1.5 flex-shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            )}

            {hasHowTo && effectiveActiveTab === 'howto' && (
              <div className="space-y-3 max-w-4xl prose prose-sm max-w-none">
                <h3 className="font-heading text-sm sm:text-base font-bold text-[#4E1225] not-prose mb-2">
                  {contentLang === 'hi' ? 'चरणबद्ध पूजा, धारण व उपयोग विधि' : 'Step-by-Step Sacred Ritual & Care'}
                </h3>
                {typeof currentHowTo === 'string' ? (
                  <div dangerouslySetInnerHTML={{ __html: currentHowTo }} />
                ) : Array.isArray(currentHowTo) ? (
                  <ul className="space-y-2.5 sm:space-y-3 not-prose">
                    {currentHowTo.map((step, idx) => (
                      <li key={idx} className="flex items-start gap-2.5 sm:gap-3 text-xs sm:text-sm text-[#2A1F16]">
                        <span className="w-5 h-5 rounded-full bg-[#6E1B34] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                          {idx + 1}
                        </span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            )}

            {hasUsp && effectiveActiveTab === 'usp' && (
              <div className="space-y-3 max-w-4xl prose prose-sm max-w-none">
                <h3 className="font-heading text-sm sm:text-base font-bold text-[#4E1225] not-prose mb-2">
                  {contentLang === 'hi' ? 'उत्पाद की मुख्य विशेषताएं व विशिष्टता (USP)' : 'Unique Selling Proposition (USP)'}
                </h3>
                {typeof currentUsp === 'string' ? (
                  <div dangerouslySetInnerHTML={{ __html: currentUsp }} />
                ) : Array.isArray(currentUsp) ? (
                  <ul className="space-y-2 not-prose">
                    {currentUsp.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm text-[#2A1F16]">
                        <span className="w-1.5 h-1.5 bg-[#D9711F] rounded-full mt-1.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            )}

            {/* Sacred Vedic Mantra Block (Point 13 - Optional) */}
            {product.mantra && (
              <div className="mt-6 sm:mt-8 bg-[#4E1225] rounded-xl sm:rounded-2xl p-4 sm:p-7 text-center text-[#FBF4E7] relative overflow-hidden">
                <span className="text-[10px] sm:text-xs font-bold tracking-[0.2em] text-[#E8D5A8] uppercase block mb-1.5 sm:mb-2">
                  Sacred Vedic Mantra
                </span>
                <p className="font-deva text-base sm:text-xl text-white font-semibold leading-relaxed mb-1.5">
                  {product.mantra}
                </p>
                {product.mantra_translit && (
                  <p className="text-[11px] sm:text-xs italic text-[#E8D5A8] mb-2.5">
                    "{product.mantra_translit}"
                  </p>
                )}
                {product.mantra_note && (
                  <p className="text-[10px] sm:text-xs text-[#E8D5A8]/80 max-w-md mx-auto">
                    {product.mantra_note}
                  </p>
                )}
              </div>
            )}

          </div>
        </section>

        {/* ═══════════════════ WHY PARAMPARIK PUJA ═══════════════════ */}
        <section className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-5 sm:py-8">
          <div className="text-center mb-5 sm:mb-7">
            <span className="text-[11px] sm:text-xs font-bold text-[#D9711F] uppercase tracking-widest">Our Sacred Promise</span>
            <h2 className="font-heading text-lg sm:text-2xl font-bold text-[#4E1225] mt-1">Why Paramparik Puja</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3.5 sm:gap-6">
            <div className="bg-white rounded-2xl p-4 sm:p-5 text-center border border-[#E7D9BC] shadow-xs">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#F3E7D0] text-[#6E1B34] flex items-center justify-center mx-auto mb-2.5">
                <CheckCircle2 className="w-5 h-5 text-[#2E7D45]" />
              </div>
              <h3 className="font-heading text-xs sm:text-sm font-bold text-[#4E1225] mb-1">Handpicked &amp; Blessed</h3>
              <p className="text-[11px] sm:text-xs text-[#83725B]">Every sacred item is authentically sourced and checked for purity.</p>
            </div>

            <div className="bg-white rounded-2xl p-4 sm:p-5 text-center border border-[#E7D9BC] shadow-xs">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#F3E7D0] text-[#6E1B34] flex items-center justify-center mx-auto mb-2.5">
                <Sparkles className="w-5 h-5 text-[#D9711F]" />
              </div>
              <h3 className="font-heading text-xs sm:text-sm font-bold text-[#4E1225] mb-1">Vedic Energisation</h3>
              <p className="text-[11px] sm:text-xs text-[#83725B]">Prana Pratishtha rituals conducted by qualified Himalayan pandits.</p>
            </div>

            <div className="bg-white rounded-2xl p-4 sm:p-5 text-center border border-[#E7D9BC] shadow-xs">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#F3E7D0] text-[#6E1B34] flex items-center justify-center mx-auto mb-2.5">
                <Package className="w-5 h-5 text-[#6E1B34]" />
              </div>
              <h3 className="font-heading text-xs sm:text-sm font-bold text-[#4E1225] mb-1">Secure Packaging</h3>
              <p className="text-[11px] sm:text-xs text-[#83725B]">Carefully packed with holy flowers and protective cushioning.</p>
            </div>

            <div className="bg-white rounded-2xl p-4 sm:p-5 text-center border border-[#E7D9BC] shadow-xs">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#F3E7D0] text-[#6E1B34] flex items-center justify-center mx-auto mb-2.5">
                <Truck className="w-5 h-5 text-[#6E1B34]" />
              </div>
              <h3 className="font-heading text-xs sm:text-sm font-bold text-[#4E1225] mb-1">Pan-India Express</h3>
              <p className="text-[11px] sm:text-xs text-[#83725B]">Fast, reliable shipping delivered safely to your home altar.</p>
            </div>
          </div>
        </section>

        {/* ═══════════════════ DYNAMIC CUSTOMER REVIEWS & TESTIMONIALS (Point 8) ═══════════════════ */}
        <section id="reviews-section" className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-5 sm:py-8">
          <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-8 border border-[#E7D9BC] shadow-xs">
            <div className="text-center mb-5 sm:mb-7">
              <span className="text-[11px] sm:text-xs font-bold text-[#D9711F] uppercase tracking-widest">Verified Feedback</span>
              <h2 className="font-heading text-lg sm:text-2xl font-bold text-[#4E1225] mt-1">What Our Customers Say</h2>
              
              <div className="flex items-center justify-center gap-3 mt-2.5">
                <span className="font-heading text-2xl sm:text-3xl font-bold text-[#4E1225]">{product.rating || 4.7}</span>
                <div className="text-left">
                  <div className="flex text-[#D9711F]">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-current" />
                    ))}
                  </div>
                  <span className="text-[10px] sm:text-xs text-[#83725B]">Based on {(product.ratingCount || 1842).toLocaleString()} verified ratings</span>
                </div>
              </div>
            </div>

            {/* Testimonials Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 sm:gap-6">
              {testimonialsList.map((testi, idx) => {
                const initial = testi.name ? testi.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'PS';
                return (
                  <div key={idx} className="bg-[#FBF4E7] p-3.5 sm:p-4 rounded-xl border border-[#E7D9BC] flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-[#6E1B34] text-white font-bold text-xs flex items-center justify-center">
                            {initial}
                          </div>
                          <div>
                            <h4 className="text-xs font-bold text-[#2A1F16]">{testi.name}</h4>
                            <span className="text-[10px] text-[#83725B]">{testi.location}</span>
                          </div>
                        </div>
                        <div className="flex text-[#D9711F]">
                          {[...Array(testi.rating || 5)].map((_, i) => (
                            <Star key={i} className="w-3 h-3 fill-current" />
                          ))}
                        </div>
                      </div>
                      <p className="text-xs text-[#2A1F16] leading-relaxed italic mb-2">
                        "{testi.quote}"
                      </p>
                    </div>
                    <div className="flex items-center gap-1 text-[10px] font-bold text-[#2E7D45] pt-1.5 border-t border-[#E7D9BC]/60">
                      <CheckCircle2 className="w-3 h-3" />
                      <span>Verified Devotee Purchase</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ═══════════════════ DYNAMIC FREQUENTLY ASKED QUESTIONS (Point 8) ═══════════════════ */}
        <section className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-5 sm:py-8">
          <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-8 border border-[#E7D9BC] shadow-xs">
            <div className="text-center mb-5 sm:mb-7">
              <span className="text-[11px] sm:text-xs font-bold text-[#D9711F] uppercase tracking-widest">Questions</span>
              <h2 className="font-heading text-lg sm:text-2xl font-bold text-[#4E1225] mt-1">Frequently Asked Questions</h2>
            </div>

            <div className="max-w-3xl mx-auto space-y-2">
              {faqsList.map((faq, idx) => (
                <div key={idx} className="border border-[#E7D9BC] rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full text-left p-3 sm:p-4 font-semibold text-xs sm:text-sm text-[#2A1F16] flex items-center justify-between gap-3 bg-[#FBF4E7]/40 hover:bg-[#FBF4E7] transition-colors"
                  >
                    <span>{faq.question || faq.q}</span>
                    <ChevronDown className={`w-3.5 h-3.5 text-[#6E1B34] transition-transform flex-shrink-0 ${openFaq === idx ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === idx && (
                    <div className="p-3 sm:p-4 text-xs text-[#83725B] leading-relaxed bg-white border-t border-[#E7D9BC]">
                      {faq.answer || faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════ RELATED PRODUCTS (Single product on mobile view) ═══════════════════ */}
        {relatedProducts.length > 0 && (
          <section className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-5 sm:py-8">
            <div className="text-center mb-5 sm:mb-7">
              <span className="text-[11px] sm:text-xs font-bold text-[#D9711F] uppercase tracking-widest">Explore More</span>
              <h2 className="font-heading text-lg sm:text-2xl font-bold text-[#4E1225] mt-1">You May Also Like</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3.5 sm:gap-6">
              {relatedProducts.map((rel, i) => (
                <ProductCard key={rel.id} product={rel} index={i} />
              ))}
            </div>
          </section>
        )}

      </div>

      {/* ═══════════════════ STICKY MOBILE BUY BAR ═══════════════════ */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-[#E7D9BC] p-2.5 shadow-2xl flex items-center justify-between gap-2.5">
        <div className="flex flex-col leading-tight pl-1">
          <span className="font-price font-bold text-base text-[#4E1225]">
            ₹{effectiveUnitPrice.toLocaleString('en-IN')}
          </span>
          {mrp > product.price && (
            <span className="text-[10px] text-[#83725B] line-through font-price">
              ₹{(mrp + addonsTotal).toLocaleString('en-IN')}
            </span>
          )}
        </div>
        <div className="flex gap-2 flex-1 max-w-[250px]">
          <button
            type="button"
            onClick={handleAddToCart}
            className="flex-1 py-2 rounded-xl border border-[#6E1B34] text-[#6E1B34] font-bold text-xs hover:bg-[#6E1B34] hover:text-white transition-all active:scale-95 truncate"
          >
            Add to Cart
          </button>
          <button
            type="button"
            onClick={handleBuyNow}
            className="flex-1 py-2 rounded-xl bg-[#6E1B34] text-white font-bold text-xs hover:bg-[#4E1225] transition-all shadow-md active:scale-95 truncate"
          >
            Buy Now
          </button>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default ProductDetailPage;
